import {
    ChangeDetectionStrategy,
    Component,
    inject,
    Signal,
    signal,
    ViewEncapsulation
} from "@angular/core";
import {
    LgTranslateDirective,
    LgTranslateService,
    useTranslationNamespace
} from "@logex/framework/lg-localization";
import type { LgGlobalShortcutRegistration } from "./lg-global-shortcuts.service";
import { Observable, Subject } from "rxjs";
import { LgArrowNavigationDirective } from "@logex/framework/ui-core";
import { detectPlatform } from "@logex/framework/utilities";

type ShortcutEntry = {
    name: string;
    key: string;
    standard: boolean;
    original: LgGlobalShortcutRegistration;
};

@Component({
    selector: "lg-global-shortcuts-popup",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LgTranslateDirective, LgArrowNavigationDirective],
    encapsulation: ViewEncapsulation.Emulated,
    templateUrl: "./lg-global-shortcuts-popup.component.html",
    styleUrls: ["./lg-global-shortcuts-popup.component.scss"],
    viewProviders: [useTranslationNamespace("FW._Shortcuts")],
    host: {
        "(document:keydown.Esc)": "_hideSelf($event)",
        "(document:keydown./)": "_hideSelf($event)"
    }
})
export class LgGlobalShortcutsPopupComponent {
    private _translate = inject(LgTranslateService);
    protected _shortcuts = signal<ShortcutEntry[]>([]);
    protected _result = new Subject<LgGlobalShortcutRegistration>();

    _initialize(
        shortcuts: LgGlobalShortcutRegistration[]
    ): Observable<LgGlobalShortcutRegistration> {
        this._shortcuts.set(this._transform(shortcuts));
        return this._result.asObservable();
    }

    protected _hideSelf(event: KeyboardEvent): void {
        event.stopPropagation();
        event.preventDefault();
        this._result.complete();
    }

    protected _triggerShortcut(event: Event, shortcut: ShortcutEntry): void {
        event.stopPropagation();
        event.preventDefault();
        if (shortcut.original.enabled()) {
            this._result.next(shortcut.original);
            this._result.complete();
        }
    }

    private _transform(shortcuts: LgGlobalShortcutRegistration[]): ShortcutEntry[] {
        const platform = detectPlatform();
        const isApple = platform === "MacOS" || platform === "iOS";
        shortcuts = [
            ...shortcuts,
            {
                key: "Esc",
                localizationKey: "FW._Shortcuts.Close",
                standard: true,
                ctrlKey: false,
                altKey: false,
                shiftKey: false,
                name: "Standard.Esc",
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                action: () => {},
                enabled: signal(true)
            }
        ];
        shortcuts.sort((a, b) => {
            // Sort by standard shortcuts first, then by key
            if (a.standard && !b.standard) return -1;
            if (!a.standard && b.standard) return 1;
            return a.key.localeCompare(b.key);
        });
        return shortcuts
            .filter((shortcut, index) => {
                // Filter out duplicates based on key and modifiers
                return (
                    index ===
                    shortcuts.findIndex(
                        s =>
                            (!!shortcut.name && s.name === shortcut.name) ||
                            (!shortcut.name &&
                                s.key === shortcut.key &&
                                s.altKey === shortcut.altKey &&
                                s.ctrlKey === shortcut.ctrlKey &&
                                s.shiftKey === shortcut.shiftKey)
                    )
                );
            })
            .map(shortcut => {
                const parts = [];
                if (shortcut.shiftKey) parts.push("Shift");
                if (shortcut.ctrlKey) parts.push(isApple ? "Control" : "Ctrl");
                if (shortcut.altKey) parts.push(isApple ? "Option" : "Alt");
                parts.push(shortcut.key);
                return {
                    name: this._translate.translate(shortcut.localizationKey ?? "?"),
                    key: parts.join(" + "),
                    standard: !!shortcut.standard,
                    original: shortcut
                };
            });
    }
}
